import { Link } from 'react-router-dom';
import { RefreshCw, CreditCard, Clock, Mail, Truck, Ban, RotateCcw, AlertTriangle, GraduationCap } from 'lucide-react';
import SEO from '../components/shared/SEO';

const SubscriptionCancellationPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="subscription-cancellation-policy-page">
      <SEO 
        title="Subscription Cancellation Policy | Queen B Logistics"
        description="Subscription Cancellation Policy for Queen B Holdings LLC, DBA Queen B Logistics - Learn how to manage and cancel your subscriptions."
      />
      
      {/* Header */}
      <section className="py-16 bg-[#050505] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
            Subscription Cancellation Policy
          </h1>
          <p className="text-white/60">
            Queen B Holdings LLC, DBA Queen B Logistics<br />
            <span className="text-white/40">Last Updated: February 2026</span>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert max-w-none">
            
            <PolicySection number="1" title="Subscription Structure" icon={CreditCard}>
              <p>After authority activation and completion of the Build phase, clients may select:</p>
              <ul>
                <li><strong>Compliance Monitoring Only</strong></li>
                <li><strong>Compliance + Dispatch Bundle</strong></li>
              </ul>
              <p>Subscriptions renew automatically on a monthly basis unless canceled.</p>
            </PolicySection>

            <PolicySection number="2" title="How to Cancel" icon={Mail}>
              <p>Clients may cancel by:</p>
              <ul>
                <li>Submitting a written cancellation request via email</li>
                <li>Using the Hive portal cancellation option (if enabled)</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 my-4">
                <p className="text-amber-400 m-0"><strong>Cancellation must be submitted at least 48 hours before the next billing date to avoid renewal.</strong></p>
              </div>
            </PolicySection>

            <PolicySection number="3" title="Grace Period Clarification" icon={Clock}>
              <p>After authority activation:</p>
              <ul>
                <li>A <strong className="text-[#D4AF37]">14-day grace period</strong> begins</li>
                <li>No billing occurs until a plan is selected and confirmed</li>
              </ul>
              <p>If no plan is selected within 14 days:</p>
              <ul>
                <li>Protect and Grow features may be restricted</li>
                <li>Account remains accessible in limited mode</li>
              </ul>
              <div className="bg-green-500/10 border border-green-500/30 p-4 my-4">
                <p className="text-green-400 m-0 font-bold">No automatic enrollment occurs without explicit user confirmation.</p>
              </div>
            </PolicySection>

            <PolicySection number="4" title="After Cancellation">
              <p>Upon cancellation:</p>
              <ul>
                <li>Subscription access remains active until the end of the paid billing cycle</li>
                <li>Features tied to subscription tier will be locked after cycle ends</li>
                <li>Historical data may remain stored for record purposes</li>
              </ul>
            </PolicySection>

            <PolicySection number="5" title="Re-Activation" icon={RotateCcw}>
              <p>Clients may re-activate services at current pricing rates.</p>
              <div className="bg-white/5 border border-white/10 p-4 my-4">
                <p className="text-white/70 m-0">Previous pricing is not guaranteed upon reactivation.</p>
              </div>
            </PolicySection>

            <PolicySection number="6" title="Multi-Truck Adjustments" icon={Truck}>
              <p>If a client reduces fleet size:</p>
              <ul>
                <li>Pricing adjustments may take effect in the next billing cycle</li>
                <li>No retroactive prorating applies</li>
              </ul>
            </PolicySection>

            <PolicySection number="7" title="Queen B Academy (Non-Subscription)" icon={GraduationCap}>
              <p>Queen B Academy courses are <strong>one-time purchases</strong>, not subscriptions.</p>
              <div className="bg-red-500/10 border border-red-500/30 p-4 my-4">
                <p className="text-red-400 m-0 font-bold">Academy purchases are non-refundable and cannot be canceled.</p>
              </div>
              <ul>
                <li>Course access is granted immediately upon purchase</li>
                <li>There is no recurring billing for Academy courses</li>
                <li>Access codes do not expire once redeemed</li>
                <li>Course content remains accessible indefinitely</li>
              </ul>
              <p className="text-white/50">For questions about Academy purchases, contact support@queenblogistics.com</p>
            </PolicySection>

            <PolicySection number="8" title="Company-Initiated Termination" icon={AlertTriangle}>
              <p>Queen B Logistics reserves the right to suspend or terminate subscriptions for:</p>
              <ul>
                <li>Non-payment</li>
                <li>Regulatory violations</li>
                <li>Fraud</li>
                <li>Abuse of service</li>
                <li>Repeated broker complaints</li>
              </ul>
            </PolicySection>

          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="text-[#D4AF37] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const PolicySection = ({ number, title, icon: Icon, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-8 h-8 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-bold text-sm">
        {number}
      </span>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase m-0">
        {title}
      </h2>
      {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
    </div>
    <div className="text-white/70 leading-relaxed pl-11">
      {children}
    </div>
  </div>
);

export default SubscriptionCancellationPolicyPage;
